import { forwardRef, useMemo } from 'react';

import { MenuItem, TextField } from '@mui/material';

import { usePickList } from 'state/selectedProject/selectedProjectHooks';
import { type InputPropertyBaseProps } from 'types/property/InputPropertyBaseProps';
import { getDisplayName } from 'utils/names';

export interface InputPropertyPicklistProps extends InputPropertyBaseProps {
  propertyId: string;
}

type Option = { key: string; label: string; index: number };

export const InputPropertyPicklist = forwardRef<HTMLInputElement, InputPropertyPicklistProps>(
  function InputPropertyPicklist({ onValueChange, propertyId, focused, value, ...props }, ref) {
    const picklist = usePickList(propertyId);

    const options = useMemo((): Option[] => {
      return Object.entries(picklist?.values ?? {})
        .filter(([, propertyValue]) => !propertyValue.isArchived)
        .map(([key, propertyValue]) => ({
          key,
          label: getDisplayName(propertyValue.valueNames),
          index: propertyValue.index
        }))
        .sort((a, b) => a.index - b.index);
    }, [picklist]);

    return (
      <TextField
        focused={focused}
        inputRef={ref}
        value={value ?? ''}
        onChange={(event) => onValueChange?.(event.target.value)}
        {...props}
        select
      >
        {options.map((option) => (
          <MenuItem key={option.key} value={option.key}>
            {option.label}
          </MenuItem>
        ))}

        {!options.length && <MenuItem disabled>No options</MenuItem>}
      </TextField>
    );
  }
);

InputPropertyPicklist.displayName = 'InputPropertyPicklist';
